/**
 * History Rail — non-destructive restore (HR-3).
 *
 * THE POINT OF THIS FILE IS THAT THERE IS NO CONFIRM DIALOG.
 *
 * The modal it replaces asked "This replaces your current project. Your current
 * state will be lost unless you saved it first." — a dialog describing a risk
 * instead of removing it. Here the risk is removed:
 *
 *   1. your current work is saved as a real version FIRST (if it is dirty)
 *   2. the old version is applied
 *   3. the result is saved as ANOTHER new version, labelled "Restored from vN"
 *
 * Nothing is overwritten, the past is not rewound, and the whole operation
 * collapses into an undo toast — which is the house rule (`builder/CLAUDE.md`:
 * "the undo IS the confirmation — never a success toast beside it"). Cloud
 * version restore was the last mutation in the builder that had not been
 * converted; `ui/history-panel.js` proved the pattern against whole-project
 * state two initiatives ago.
 *
 * THE 409 TRAP, and why step 2.5 exists. `loadFromZip` sets `cloudVersion` from
 * the BUNDLE, so after applying v14 the store believes its sync base is 14.
 * `cloudSave` sends `baseVersion: state.cloudVersion`, and the server 409s
 * because its latest is 19. The base has to be re-pointed at the project's
 * LATEST before saving — never at the version just restored. This is the rule
 * `project-history-modal.js:1386` records, carried across verbatim because
 * losing it guarantees a self-conflict on the very next save.
 */

import { store } from '../../core/state.js';
import { cloudSave, loadFromZip, isLoadSupersededError } from '../../core/save-load.js';
import { getProjectVersionBundle } from '../../templates/auth-api.js';
import { showTransactionalUndoToast, showToast } from '../toast.js';
import { reportError } from '../../core/report-error.js';
import { TemplateApiError } from '../../templates/template-api.js';

const UNDO_ID = 'undo-history-restore';

/**
 * @param {object} opts
 * @param {string} opts.projectId
 * @param {() => void} opts.onBeforeApply — return the canvas to live state
 * @param {() => Promise<void>|void} opts.onApplied — refresh the timeline
 * @param {object} [opts.deps]
 */
export function createRestoreController({ projectId, onBeforeApply, onApplied, deps = {} }) {
  const save = deps.cloudSave || cloudSave;
  const fetchBundle = deps.getProjectVersionBundle || getProjectVersionBundle;
  const applyZip = deps.loadFromZip || loadFromZip;
  const superseded = deps.isLoadSupersededError || isLoadSupersededError;
  const undoToast = deps.showTransactionalUndoToast || showTransactionalUndoToast;
  const toast = deps.showToast || showToast;
  const report = deps.reportError || reportError;
  const liveStore = deps.store || store;

  /**
   * Single-flight across the WHOLE rail, not per-button. There is only one
   * restore control now, but the guard stays on the function: the modal needed
   * exactly this after a click on v5 followed by a click on v4 ran two
   * whole-project restores, and delegation outlives layouts.
   */
  let inFlight = false;

  /** The one apply path. Undo is a plain restore of another version, not a second implementation. */
  async function applyVersion(version, { label }) {
    const blob = await fetchBundle(projectId, version);
    await applyZip(blob, { requireWritable: true });
    return label;
  }

  /** Re-point the sync base at the project's LATEST. See the 409 trap above. */
  async function rebaseTo(latest) {
    const live = liveStore.getState();
    if (!live || live.projectId !== projectId || !Number.isInteger(latest)) return;
    live.cloudVersion = latest;
    try {
      await liveStore.saveToIDB();
    } catch {
      // Best-effort: the autosave slot re-syncs on the next mutation. The
      // in-memory value is what the next cloudSave reads.
    }
  }

  return {
    get busy() {
      return inFlight;
    },

    /**
     * @param {number} version
     * @returns {Promise<{ok: boolean, restored?: number, undoTarget?: number|null}>}
     */
    async restore(version) {
      if (inFlight) return { ok: false };
      inFlight = true;
      try {
        // The canvas is showing a DETACHED state. It has to come back to live
        // before the store changes underneath it, or the render in flight
        // outlives the state it was rendering.
        onBeforeApply?.();

        const before = liveStore.getState();
        if (!before || before.projectId !== projectId) return { ok: false };

        // ── 1. preserve ──────────────────────────────────────
        // The undo target is a REAL version either way: the one we are about to
        // mint if there is unsaved work, or the project's current latest if
        // there is not. Undo therefore never needs a snapshot of its own.
        let undoTarget = Number(before.cloudVersion) || null;
        let latest = undoTarget;
        if (liveStore.isDirty()) {
          try {
            const saved = await save({ label: 'Before restore' });
            const minted = Number(saved?.project?.latestVersion);
            if (Number.isInteger(minted)) {
              undoTarget = minted;
              latest = minted;
            }
          } catch (err) {
            // Nothing has been applied yet, so this is a clean abort.
            report('History restore preserve', err, {
              user: 'toast',
              message:
                err instanceof TemplateApiError
                  ? `Could not save your current work first — ${err.message}`
                  : 'Could not save your current work first, so nothing was restored'
            });
            return { ok: false };
          }
        }

        // ── 2. apply ─────────────────────────────────────────
        try {
          await applyVersion(version, { label: `Restored from v${version}` });
        } catch (err) {
          if (superseded(err)) {
            // Expected-flow: another tab won the race. Not an error, and the
            // preserve save above means nothing was lost.
            toast(err.message, 'info');
            return { ok: false };
          }
          report('History restore apply', err, {
            user: 'toast',
            message:
              err instanceof TemplateApiError
                ? `Could not restore v${version} — ${err.message}`
                : `Could not restore v${version}`
          });
          return { ok: false };
        }

        // ── 2.5 re-base, then 3. mint ────────────────────────
        await rebaseTo(latest);
        try {
          await save({ label: `Restored from v${version}` });
        } catch (err) {
          // NOT a rollback, and deliberately so. The old version is already
          // applied locally and the pre-restore work is already a real version,
          // so nothing is lost — what failed is only recording the restore as
          // its own version, which the next save will do. Rolling back here
          // would mean a second whole-project load that can fail in exactly the
          // same way, turning one bad outcome into two.
          report('History restore save', err, {
            user: 'toast',
            message: `Restored v${version}, but saving it to the cloud failed — it is applied locally`
          });
        }

        await onApplied?.();

        // ── 5. the undo IS the confirmation ──────────────────
        // Not `showUndoToast`: that one steps LOCAL patch history, and this is a
        // whole-project swap. The way back is a plain restore of the version we
        // preserved — the same code path, so there is no second implementation
        // to keep correct.
        undoToast(`Restored v${version}`, {
          seconds: 8,
          id: UNDO_ID,
          onUndo: () => {
            if (!Number.isInteger(undoTarget)) return;
            void (async () => {
              try {
                await applyVersion(undoTarget, { label: 'Undo restore' });
                await rebaseTo(latest);
                await onApplied?.();
              } catch (err) {
                report('History restore undo', err, {
                  user: 'toast',
                  message: 'Could not undo the restore'
                });
              }
            })();
          }
        });

        return { ok: true, restored: version, undoTarget };
      } finally {
        inFlight = false;
      }
    }
  };
}
